import { Box } from '@ltvco/refresh-lib/theme';
import {
  useSessionEscalation,
  useSession,
  useServiceToken,
} from '@ltvco/refresh-lib/v1';
import { useEffect, useState } from 'react';

import { useQueryParams } from 'utils/useQueryParams';

import AuthenticatedContent from './AuthenticatedContent';
import { ErrorModal } from './ErrorModal/ErrorModal';
import { GettingStarted } from './GettingStarted/GettingStarted';
import { KnowledgeBasedAuthentication } from './KnowledgeBasedAuthentication/KnowledgeBasedAuthentication';
import { Loading } from './Loading';
import { sessionEscalationURL } from './Oxford/constants';
import { useOneTimePasscodeContext } from './Oxford/OneTimePasscodeContext';
import { useOxford } from './Oxford/Oxford';
import { UseAuthentication } from './Oxford/useAuthentication';
import { SignUpForm } from './SignUp/SignUpForm';

export const IdMonitorContainer = () => {
  document.title = 'ID Monitor - BeenVerified';

  const [sentOneTimePasscodeCount, setSentOneTimePasscodeCount] = useState(0);

  const escalatedParam = useQueryParams().get('escalated');

  const { checkEmail, sessionAuthorized, checkSessionEscalation } =
    useSessionEscalation();
  const { refetch: getSessionEscalation } = checkSessionEscalation;

  const {
    getUserProfile,
    getAuthenticationQuestions,
    getAuthenticationCheckAnswers,
    setEnabledUserProfileQuery,
    isLoading: isOxfordLoading,
    isOxfordError,
    enabledUserProfileQuery,
  } = useOxford();

  const { data: userProfileData } = getUserProfile;
  const { data: authenticationQuestionsData } = getAuthenticationQuestions;
  const { data: authenticationCheckAnswersData } =
    getAuthenticationCheckAnswers;
  const authenticationResponse =
    authenticationQuestionsData ?? authenticationCheckAnswersData;

  const {
    session: { account },
  } = useSession();

  const { blackBoxId } = useOneTimePasscodeContext();

  const isEnrolled =
    account?.account.user_settings.credit_hub_enrollment_completed;

  const { refetch: refetchServiceToken, data: tokenData } = useServiceToken(
    'oxford',
    sessionEscalationURL
  );

  /**
   * Effect hook that handles session escalation based on enrollment status and escalation parameter.
   * Triggers session escalation check when either:
   * - User is enrolled in credit hub
   * - URL contains escalated=true parameter
   *
   * @dependencies {boolean} escalatedParam - URL parameter indicating if session should be escalated
   * @dependencies {boolean} isEnrolled - User's credit hub enrollment status
   * @dependencies {boolean} checkEmail - Current email check status
   * @dependencies {Function} getSessionEscalation - Function to trigger session escalation
   */
  useEffect(() => {
    if ((isEnrolled || escalatedParam === 'true') && !checkEmail) {
      getSessionEscalation();
    }
  }, [escalatedParam, isEnrolled, checkEmail, getSessionEscalation]);

  /**
   * Effect hook that manages service token fetching based on session authorization.
   * Automatically fetches a new service token when session is authorized but no token exists.
   *
   * @dependencies {boolean} sessionAuthorized - Current session authorization status
   * @dependencies {Function} refetchServiceToken - Function to fetch new service token
   * @dependencies {Object} tokenData - Current service token data
   */
  useEffect(() => {
    if (sessionAuthorized && !tokenData) {
      refetchServiceToken();
    }
  }, [sessionAuthorized, refetchServiceToken, tokenData]);

  /**
   * Effect hook that enables user profile query when all required conditions are met.
   * Enables the query when:
   * - Service token is available
   * - BlackBox ID is defined
   * - Session is authorized
   *
   * @dependencies {Object} tokenData - Service token data
   * @dependencies {Function} setEnabledUserProfileQuery - Function to enable/disable profile query
   * @dependencies {string|undefined} blackBoxId - Current BlackBox ID
   * @dependencies {boolean} sessionAuthorized - Current session authorization status
   */
  useEffect(() => {
    if (tokenData?.token && blackBoxId !== undefined && sessionAuthorized) {
      setEnabledUserProfileQuery(true);
    }
  }, [tokenData, setEnabledUserProfileQuery, blackBoxId, sessionAuthorized]);

  const increaseSentOneTimePasscodeCount = () => {
    setSentOneTimePasscodeCount(sentOneTimePasscodeCount + 1);
  };

  /**
   * Determines and renders the appropriate component based on the current authentication and user state.
   * This method handles multiple authentication scenarios including:
   * - Session authorization
   * - Oxford authentication
   * - One-time passcode verification
   * - User profile validation
   * - Knowledge-based authentication
   *
   * @returns {JSX.Element} The appropriate component to display based on the current state:
   * - AuthenticatedContent: When user is fully authenticated
   * - ErrorModal: For various error states (generic, authentication, profile issues)
   * - Loading: During loading states
   * - KnowledgeBasedAuthentication: When KBA questions are available
   * - SignUpForm: When user needs to sign up
   * - GettingStarted: Default view for unauthenticated users
   */
  const display = () => {
    if (sessionAuthorized && enabledUserProfileQuery) {
      if (isOxfordError) {
        return <ErrorModal type={'generic'} />;
      }

      if (isOxfordLoading || blackBoxId === undefined) {
        return <Loading />;
      }

      if (userProfileData !== undefined) {
        if (
          [
            userProfileData?.authenticated,
            authenticationResponse?.authenticated,
          ].includes(true)
        ) {
          return <AuthenticatedContent />;
        } else if (authenticationResponse?.next_attempt_date) {
          if (sentOneTimePasscodeCount > 0) {
            return <ErrorModal type={'oneTimePasscodeAuthenticationError'} />;
          }
          return <ErrorModal type={'authenticationAnswersIncorrect'} />;
        } else if (authenticationResponse?.update_user_profile === true) {
          return <ErrorModal type={'retryUserProfile'} />;
        } else if (authenticationResponse?.update_user_profile === false) {
          return <ErrorModal type={'failedUserProfile'} />;
        } else if (
          authenticationResponse?.in_progress === true &&
          !UseAuthentication.isOneTimePasscodeQuestion(
            authenticationResponse?.questionList
          )
        ) {
          return <ErrorModal type={'authenticationInProgress'} />;
        } else if (authenticationResponse?.questionList) {
          return (
            <KnowledgeBasedAuthentication
              sentOneTimePasscodeCount={sentOneTimePasscodeCount}
              increaseSentOneTimePasscodeCount={
                increaseSentOneTimePasscodeCount
              }
            />
          );
        }
      }

      return <SignUpForm />;
    }
    return <GettingStarted />;
  };

  return (
    <Box id="id-monitor-container" className="id-monitor-container">
      {display()}
    </Box>
  );
};
